import { formatAedCompact } from "@ncp/i18n";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";

export type FilingStatus = "OutOfScope" | "NotYetDue" | "Filed" | "FailureToNotifyCandidate" | "StandstillBreachCandidate";
export type ReviewStatus = "New" | "UnderReview" | "Dismissed" | "Linked" | "Escalated";

export interface DealParty { role: string; name: string; externalId?: string | null; country?: string | null; sector?: string | null; marketEntityId?: string | null; marketEntityName?: string | null; matchConfidence?: number | null; isUae: boolean; uaeTurnoverAed?: number | null; uaeTurnoverYear?: number | null; uaeSubsidiaryName?: string | null; stakePercent?: number | null }

export interface DealSummary {
  id: string; sourceKey: string; externalId: string; status: string; title: string; dealTypes: string[]; announcedOn?: string | null; completedOn?: string | null;
  expectedCloseOn?: string | null; lastEditAt?: string | null; providerStatus?: string | null; providerUrl?: string | null;
  valueAed?: number | null; valueMillions?: number | null; valueCurrency?: string | null; valueEstimated?: boolean; enterpriseValueMillions?: number | null; equityValueMillions?: number | null; targetStakePercent?: number | null; stakeExact?: boolean | null;
  nexus: string; controlVerdict: string; thresholdVerdict: string; combinedUaeTurnoverAed?: number | null; combinedMarketSharePercent?: number | null;
  filingStatus: FilingStatus; matchedCaseId?: string | null; matchedCaseNumber?: string | null; score: number; summaryEn?: string | null; summaryAr?: string | null; screenedAt?: string | null;
  reviewStatus: ReviewStatus; reviewedAt?: string | null; reviewNote?: string | null; marketSignalId?: string | null; marketTransactionId?: string | null; caseId?: string | null; proposalId?: string | null;
  countries: string[]; sectors: string[]; isicCodes: string[]; parties: DealParty[]; isCandidate: boolean;
}

export interface DealWatchStats {
  total: number; failureToNotify: number; standstill: number; notYetDue: number; filed: number; outOfScope: number; escalated: number; awaitingReview: number; intelligence: number;
  closingSoon?: number; live?: number; completed?: number; estimated?: number;
}
export interface DealWatchView { lastSync?: string | null; recordCount?: number | null; mode: string; health?: string | null; pendingProposals: number; lastAgentRun?: string | null; stats: DealWatchStats; items: DealSummary[] }

export interface ScreeningFactor { key: string; label: string; labelAr: string; points: number; detail?: string | null }
export interface ReviewEvent { agency: string; country?: string | null; outcome?: string | null; phase?: string | null; hasRemedy: boolean; docket?: string | null; occurredOn?: string | null }
export interface DealDetail {
  summary: DealSummary; description?: string | null; settlementOn?: string | null; expectedCloseOn?: string | null; lastEditAt?: string | null; thresholdBasis?: string | null;
  providerUrl?: string | null; providerStatus?: string | null; valueEstimated?: boolean; enterpriseValueMillions?: number | null; equityValueMillions?: number | null; stakeExact?: boolean | null;
  parties: DealParty[]; factors: ScreeningFactor[]; reviewEvents: ReviewEvent[]; raw: unknown;
  matchedCase?: { id: string; caseNumber: string; title: string; state: string; status: string; submittedAt?: string | null; decision?: { outcome: string; issuedAt: string } | null } | null;
  case?: { id: string; caseNumber: string; title: string; state: string; status: string } | null;
  signal?: { id: string; signalType: string; score?: number | null; status: string; escalatedCaseId?: string | null } | null;
  proposal?: { id: string; status: string; title: string; confidence: number; createdAt: string } | null;
  lineage: Array<{ operation: string; sourceReference?: string | null; beforeJson?: string | null; afterJson?: string | null; createdAt: string }>;
  reviewedBy?: string | null; reviewNote?: string | null; reviewedAt?: string | null;
  related?: CatalogHit[];
}

export type CatalogKind = "Deal" | "News" | "Company" | "Intelligence";
export interface CatalogHit {
  kind: CatalogKind | string; replicaId?: string | null; externalId: string; headline: string; summary?: string | null;
  status?: string | null; filingStatus?: string | null; when?: string | null; url?: string | null; companies: string[]; fromReplica: boolean;
  sourceKey?: string | null;
}
export interface CatalogSearchResult { query: string; live: boolean; hits: CatalogHit[]; errors: string[] }
export interface CompanySnapshot { externalId: string; name: string; description?: string | null; country?: string | null; website?: string | null; relatedIntelCount?: number | null; url?: string | null }
export interface CompanyLookup { company?: CompanySnapshot | null; relatedDeals: CatalogHit[]; relatedNews: CatalogHit[]; errors: string[] }

export interface RegisterCase { id: string; caseNumber: string; title: string; state: string; status: string; submittedAt?: string | null }
export interface SyncResult { queued: boolean; jobId?: string; fetched?: number; inserted?: number; updated?: number; screened?: number; candidates?: number; intelligenceItems?: number; newCandidates?: number; errors?: string[] }

export interface DealFilters {
  filingStatus?: string; reviewStatus?: string; q?: string; minScore?: number; includeOutOfScope?: boolean;
  status?: string; nexus?: string; dealType?: string; sector?: string; closingSoon?: boolean; estimated?: boolean; announcedFrom?: string; sort?: string;
}

export const dealKeys = {
  all: ["deal-watch"] as const,
  list: (f: DealFilters) => ["deal-watch", "list", f] as const,
  detail: (id: string | null) => ["deal-watch", "detail", id] as const,
  catalog: (q: string, live: boolean) => ["deal-watch", "catalog", q, live] as const,
  company: (id: string | null) => ["deal-watch", "company", id] as const,
  register: (q: string) => ["deal-watch", "register", q] as const,
  byEntity: (id: string | null) => ["deal-watch", "by-entity", id] as const,
  byCase: (id: string) => ["deal-watch", "by-case", id] as const,
};

export function useDeals(filters: DealFilters) {
  const api = useApi();
  return useQuery({ queryKey: dealKeys.list(filters), queryFn: () => api.get<DealWatchView>("/api/deal-watch", { ...filters }) });
}
export function useDeal(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: dealKeys.detail(id), queryFn: () => api.get<DealDetail>(`/api/deal-watch/${id}`), enabled: !!id });
}
export function useRegisterSearch(q: string, enabled: boolean) {
  const api = useApi();
  return useQuery({ queryKey: dealKeys.register(q), queryFn: () => api.get<RegisterCase[]>("/api/deal-watch/register", { q }), enabled });
}
export function useDealsByEntity(entityId: string | null) {
  const api = useApi();
  return useQuery({ queryKey: dealKeys.byEntity(entityId), queryFn: () => api.get<DealSummary[]>(`/api/deal-watch/by-entity/${entityId}`), enabled: !!entityId });
}
export type DealRelation = "Linked" | "Escalated" | "Suggested";
export interface CaseDealRow {
  id: string; externalId: string; title: string; status: string; filingStatus: FilingStatus; reviewStatus: ReviewStatus;
  relation: DealRelation; score: number; valueAed?: number | null; targetStakePercent?: number | null;
  announcedOn?: string | null; expectedCloseOn?: string | null; completedOn?: string | null; providerUrl?: string | null;
  overlap: string[]; parties: string[]; reviewEvents: ReviewEvent[];
}
export interface CaseDealBriefing {
  caseId: string; linked: CaseDealRow[]; suggested: CaseDealRow[]; news: CatalogHit[]; checks: string[]; checksAr: string[];
}

export function useCaseDealIntelligence(caseId: string) {
  const api = useApi();
  return useQuery({ queryKey: dealKeys.byCase(caseId), queryFn: () => api.get<CaseDealBriefing>(`/api/deal-watch/by-case/${caseId}`), enabled: !!caseId });
}
export function useDealCatalog(q: string, live = true) {
  const api = useApi();
  const query = q.trim();
  return useQuery({
    queryKey: dealKeys.catalog(query, live),
    queryFn: () => api.get<CatalogSearchResult>("/api/deal-watch/catalog", { q: query, live }),
    enabled: query.length >= 2,
    staleTime: 15_000,
  });
}
export function useMergermarketCompany(companyId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: dealKeys.company(companyId),
    queryFn: () => api.get<CompanyLookup>(`/api/deal-watch/lookup/company/${encodeURIComponent(companyId!)}`),
    enabled: !!companyId,
  });
}

/** Roles allowed to review, dismiss and link deals (mirrors DealWatchEndpoints.Reviewers). */
export const DEAL_REVIEWERS = ["HeadOfComplaints", "Director", "Admin", "EconomicResearcher"];
/** Roles allowed to open an ex officio case from a deal. */
export const DEAL_ESCALATORS = ["HeadOfComplaints", "Director", "Admin"];
/** The shortest reasons a dismissal is recorded with (a sentence); the API refuses shorter ones (DealWatchService.DismissalReasonMin). */
export const DISMISSAL_REASONS_MIN = 20;

export const fmtAedShort = (v: number | null | undefined, locale: string) => formatAedCompact(v, locale === "ar" ? "ar" : "en", 2);
